"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DiamondIcon from "@mui/icons-material/Diamond";
import FlagIcon from "@mui/icons-material/Flag";
import RedFlagPopup from "../../components/dialog/redFlagPopup";
import FloatPopup from "../../components/dialog/floatPopup";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";
import ChipToken, { getChipColor } from "../../components/ChipToken";
import { maskPhone } from "../../helper";

const calcAmount = (denom, qty) => Number(denom || 0) * Number(qty || 0);

const ChipTable = ({ chips, qty, setQty, isPromo = false }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: isPromo ? "#7b1fa208" : "#f5f5f5" }}>
          <TableCell sx={{ fontWeight: 700 }}>Denomination ()</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="center">Stock Available</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="center">Qty to Issue</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {chips.length === 0 ? (
          <TableRow>
            <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
              No {isPromo ? "promotional " : ""}chip stock found
            </TableCell>
          </TableRow>
        ) : (
          chips.map((chip) => {
            const q = qty[chip.denomination] || "";
            const overStock = Number(q) > chip.quantity;
            const cc = isPromo
              ? { bg: "#7b1fa2", text: "#fff" }
              : getChipColor(chip.denomination);
            return (
              <TableRow key={chip._id} sx={{ backgroundColor: cc.bg + "18" }}>
                <TableCell>
                  <ChipToken denomination={chip.denomination} size={40} isPromo={isPromo} />
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={600} color={chip.quantity === 0 ? "error" : "success.main"}>
                    {chip.quantity}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: 120 }}>
                  <TextField
                    size="small" type="number" value={q}
                    onChange={(e) => setQty({ ...qty, [chip.denomination]: e.target.value })}
                    inputProps={{ min: 0, max: chip.quantity }}
                    disabled={chip.quantity === 0}
                    error={overStock}
                    helperText={overStock ? `Max ${chip.quantity}` : ""}
                    sx={{ width: 90, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }}
                  />
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </Paper>
);

const pad = (n) => String(n).padStart(2, "0");
const nowStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const emptyForm = () => ({ date: nowStr(), remarks: "" });

export default function ChipIssueForm({ open, onClose, refresh, initialUser = null, initialAmount = 0, hideRedFlagPopup = false }) {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  // chip stock split by type
  const [regularStockChips, setRegularStockChips] = useState([]);
  const [promoStockChips,   setPromoStockChips]   = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  // user search
  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [foundUser, setFoundUser] = useState(null);

  // issued quantities keyed by denomination
  const [issued,      setIssued]      = useState({});
  const [promoIssued, setPromoIssued] = useState({});

  // loan wallet toggle
  const [useLoanWallet, setUseLoanWallet] = useState(false);

  // other form fields
  const [form, setForm] = useState({ ...emptyForm() });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  /* ── load chip stock whenever drawer opens ── */
  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, date: nowStr() }));

    if (initialUser) {
      const lookup = initialUser.phone || initialUser.playerId || "";
      setPhone(lookup);
      const param = /^\d{10}$/.test(lookup) ? `phone=${lookup}` : `playerId=${lookup}`;
      axiosRequest("GET", `${apiBaseUrl}/user_details?${param}`, token)
        .then((res) => { setFoundUser(res?.data?.userDetails || initialUser); })
        .catch(() => setFoundUser(initialUser));
    }

    setStockLoading(true);
    axiosRequest("GET", `${apiBaseUrl}/chips`, token)
      .then((res) => {
        const chips = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? []);
        const dedupe = (arr) => { const s = {}; return arr.filter(c => s[c.denomination] ? false : (s[c.denomination] = true)); };
        const regular = dedupe(chips.filter((c) => (c.chip_type || "REGULAR") !== "PROMO"));
        const promo   = dedupe(chips.filter((c) => c.chip_type === "PROMO"));
        setRegularStockChips(regular);
        setPromoStockChips(promo);

        const initReg = {};
        regular.forEach((c) => { initReg[c.denomination] = ""; });
        setIssued(initReg);

        const initPromo = {};
        promo.forEach((c) => { initPromo[c.denomination] = ""; });
        setPromoIssued(initPromo);
      })
      .catch(() => { setRegularStockChips([]); setPromoStockChips([]); })
      .finally(() => setStockLoading(false));
  }, [open]);

  /* ── user search ── */
  const handleSearch = async () => {
    const input = phone.trim();
    if (!input) return;
    setSearching(true);
    setSearchError("");
    setFoundUser(null);
    try {
      const param = /^\d{10}$/.test(input) ? `phone=${input}` : `playerId=${input}`;
      const res = await axiosRequest("GET", `${apiBaseUrl}/user_details?${param}`, token);
      const user = res?.data?.userDetails;
      if (user) setFoundUser(user);
      else setSearchError("No user found.");
    } catch {
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  /* ── denomination helpers ── */

  const regularAmount = regularStockChips.reduce(
    (sum, c) => sum + calcAmount(c.denomination, issued[c.denomination]), 0
  );
  const promoChipAmount = promoStockChips.reduce(
    (sum, c) => sum + calcAmount(c.denomination, promoIssued[c.denomination]), 0
  );
  const totalAmount = regularAmount + promoChipAmount;

  const promoBal        = Number(foundUser?.promotional_balance || 0);
  const depositBal      = Number(foundUser?.cash_deposite || 0);
  const winningBal      = Number(foundUser?.winning_balance || 0);
  const loanBal         = Number(foundUser?.loan_amount || 0);
  const loanDistributed = Number(foundUser?.loan_distributed || 0);
  const loanLimit       = Number(foundUser?.loan_limit || 0);
  const loanCredit      = Math.max(0, loanBal - loanDistributed);

  // Promo chips always deduct from promo balance first
  const promoBalAfterPromoChips = Math.max(0, promoBal - promoChipAmount);
  const totalAvailable = promoBalAfterPromoChips + depositBal + winningBal + (useLoanWallet ? loanCredit : 0);

  // Live priority breakdown for regular chips (after promo chips reserved)
  let rem = regularAmount;
  const breakPromo   = Math.min(rem, promoBalAfterPromoChips); rem -= breakPromo;
  const breakDeposit = Math.min(rem, depositBal);              rem -= breakDeposit;
  const breakWinning = Math.min(rem, winningBal);              rem -= breakWinning;
  const breakLoan    = useLoanWallet ? Math.min(rem, loanCredit) : 0;

  const isPromoOverBalance   = foundUser && promoChipAmount > promoBal;
  const isRegularOverBalance = foundUser && regularAmount > totalAvailable;
  const isOverBalance        = isPromoOverBalance || isRegularOverBalance;

  /* ── submit ── */
  const handleSave = async () => {
    setSubmitError("");
    try {
      setLoading(true);
      const regularChips = regularStockChips
        .filter((c) => Number(issued[c.denomination]) > 0)
        .map((c) => ({ denomination: c.denomination, quantity: Number(issued[c.denomination]), chip_type: "REGULAR" }));

      const promoChips = promoStockChips
        .filter((c) => Number(promoIssued[c.denomination]) > 0)
        .map((c) => ({ denomination: c.denomination, quantity: Number(promoIssued[c.denomination]), chip_type: "PROMO" }));

      const chips = [...regularChips, ...promoChips];

      const res = await axiosRequest("POST", `${apiBaseUrl}/chip-issue`, token, {
        player_id: foundUser?.playerId || "",
        user_name: foundUser?.name || "",
        phone: foundUser?.phone || phone,
        ...form,
        date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        chips,
        total_amount: totalAmount,
        useLoanWallet,
      });

      if (res?.status === false || res?.data?.status === false) {
        setSubmitError(res?.message || res?.data?.message || "Failed to issue chips.");
        return;
      }

      refresh();
      handleClose();
    } catch (err) {
      setSubmitError("Something went wrong.");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhone("");
    setFoundUser(initialUser || null);
    setSearchError("");
    setSubmitError("");
    setIssued({});
    setPromoIssued({});
    setUseLoanWallet(false);
    setForm({ ...emptyForm() });
    onClose();
  };

  return (
    <>
    <RedFlagPopup user={open ? foundUser : null} disabled={hideRedFlagPopup} />
    <FloatPopup user={open ? foundUser : null} context="issue" disabled={hideRedFlagPopup} />
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 640, p: 3, overflowY: "auto", height: "100%" }}>

        {/* HEADER */}
        <Typography variant="h5" fontWeight={700}>Issue Chips</Typography>
        <Typography color="text.secondary" variant="body2" mb={2}>
          Search player by phone number or player ID
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* ── PHONE SEARCH ── */}
        <Typography fontWeight={600} mb={1}>Search Player</Typography>
        <Box display="flex" gap={1} mb={1}>
          <TextField
            fullWidth size="small" label="Phone / Player ID"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained" onClick={handleSearch}
            disabled={searching || !phone.trim()}
            sx={{ minWidth: 90, whiteSpace: "nowrap" }}
          >
            {searching ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
        </Box>

        {searchError && <Alert severity="error" sx={{ mb: 1 }}>{searchError}</Alert>}

        {/* Red flag warning */}
        {foundUser?.red_flag && (
          <Alert severity="error" icon={<FlagIcon />} sx={{ mb: 2, fontWeight: 600 }}>
            RED FLAG — {foundUser.red_flag_remark || "This player has been flagged"}
          </Alert>
        )}

        {/* ── USER CARD ── */}
        {foundUser && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
            <Typography fontWeight={700} variant="subtitle1">{foundUser.name || "—"}</Typography>
            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Player ID</Typography>
                <Typography fontWeight={600} variant="body2">{foundUser.playerId || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Phone</Typography>
                <Typography fontWeight={600} variant="body2">{maskPhone(foundUser.phone) || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Winning Balance</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#38ef7d" }}>
                  {winningBal.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Promo Balance</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#ce93d8" }}>
                  {promoBal.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12}><Box sx={{ borderTop: "1px solid rgba(255,255,255,0.15)", my: 1 }} /></Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Deposited</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#7ec8e3" }}>
                  {Number(foundUser.cash_deposite || 0).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Outstanding</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: loanBal > 0 ? "#ff6b6b" : "#38ef7d" }}>
                  {loanBal.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Limit</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#fff" }}>
                  {loanLimit.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Distributed</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: loanDistributed > 0 ? "#f59e0b" : "#fff" }}>
                  {loanDistributed.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Available</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: loanCredit > 0 ? "#38ef7d" : "#fff" }}>
                  {loanCredit.toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* ── REGULAR CHIP STOCK TABLE ── */}
        <Typography fontWeight={700} mb={1}>Regular Chip Stock</Typography>
        {stockLoading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : (
          <ChipTable chips={regularStockChips} qty={issued} setQty={setIssued} isPromo={false} />
        )}

        {/* ── PROMOTIONAL CHIP STOCK TABLE ── */}
        {!stockLoading && (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DiamondIcon sx={{ color: "#7b1fa2", fontSize: 20 }} />
              <Typography fontWeight={700}>Promotional Chip Stock</Typography>
              <Chip label="PROMO" size="small" sx={{ bgcolor: "#7b1fa2", color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
              <Typography variant="caption" color="text.secondary">— deducts from Promo Balance only</Typography>
            </Box>
            {isPromoOverBalance && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Promo chips ({promoChipAmount.toLocaleString()}) exceed available promo balance ({promoBal.toLocaleString()})
              </Alert>
            )}
            <ChipTable chips={promoStockChips} qty={promoIssued} setQty={setPromoIssued} isPromo={true} />
          </>
        )}

        {/* ── LOAN WALLET CHECKBOX ── */}
        {foundUser && loanCredit > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: useLoanWallet ? "#f59e0b" : "#e0e0e0" }}>
            <Checkbox
              checked={useLoanWallet}
              onChange={(e) => setUseLoanWallet(e.target.checked)}
              size="small" color="warning"
            />
            <Typography component="span" variant="body2" fontWeight={600}>Use Loan Wallet &nbsp;</Typography>
            <Typography component="span" variant="body2" color="text.secondary">
              (Available credit: {loanCredit.toLocaleString()} — last priority)
            </Typography>
          </Paper>
        )}

        {/* ── DEDUCTION BREAKDOWN ── */}
        {foundUser && totalAmount > 0 && (
          <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
            {promoChipAmount > 0 && (
              <Box display="flex" justifyContent="space-between" alignItems="center"
                sx={{ px: 2, py: 1.2, borderBottom: "1px solid #f0f0f0", bgcolor: "#7b1fa208" }}>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="#7b1fa2">Promo Chips → Promo Balance</Typography>
                  <Typography variant="caption" color="text.secondary">Available: {promoBal.toLocaleString()}</Typography>
                </Box>
                <Typography fontWeight={700} color="#7b1fa2">− {promoChipAmount.toLocaleString()}</Typography>
              </Box>
            )}
            {[
              { label: "1. Promo Wallet",   avail: promoBalAfterPromoChips, deduct: breakPromo,   color: "#9c27b0" },
              { label: "2. Deposit Wallet", avail: depositBal,              deduct: breakDeposit, color: "#1976d2" },
              { label: "3. Winning Wallet", avail: winningBal,              deduct: breakWinning, color: "#388e3c" },
              { label: "4. Loan Wallet",    avail: loanCredit,              deduct: breakLoan,    color: "#f59e0b", disabled: !useLoanWallet },
            ].map(({ label, avail, deduct, color, disabled }) => (
              <Box key={label} display="flex" justifyContent="space-between" alignItems="center"
                sx={{ px: 2, py: 1.2, borderBottom: "1px solid #f0f0f0", opacity: disabled ? 0.4 : 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600} color={color}>{label}</Typography>
                  <Typography variant="caption" color="text.secondary">Available: {avail.toLocaleString()}</Typography>
                </Box>
                <Typography fontWeight={700} color={deduct > 0 ? color : "text.disabled"}>
                  {deduct > 0 ? `− ${deduct.toLocaleString()}` : "—"}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* ── TOTAL SUMMARY ── */}
        <Paper sx={{
          p: 2, mb: 3, borderRadius: 2,
          background: isOverBalance
            ? "linear-gradient(135deg,#b71c1c,#e53935)"
            : "linear-gradient(135deg,#11998e,#38ef7d)",
          color: "#fff",
        }}>
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Chips to Issue</Typography>
              <Typography variant="h5" fontWeight={700}>{totalAmount.toLocaleString()}</Typography>
              {promoChipAmount > 0 && (
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  (Regular: {regularAmount.toLocaleString()} + Promo: {promoChipAmount.toLocaleString()})
                </Typography>
              )}
            </Grid>
            {foundUser && (
              <Grid item xs={6} textAlign="right">
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Regular Available</Typography>
                <Typography variant="h5" fontWeight={700}>{totalAvailable.toLocaleString()}</Typography>
              </Grid>
            )}
          </Grid>
          {isOverBalance && (
            <Typography variant="caption" mt={0.5} display="block">
              ⚠ {isPromoOverBalance ? "Promo chips exceed promo balance!" : "Total exceeds available balance!"}
            </Typography>
          )}
        </Paper>

        {/* ── OTHER FIELDS ── */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date & Time"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} size="small" label="Remarks (optional)"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
          </Grid>

          {submitError && (
            <Grid item xs={12}><Alert severity="error">{submitError}</Alert></Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained" onClick={handleSave}
                disabled={
                  loading || !foundUser || !form.date || totalAmount === 0 || isOverBalance ||
                  regularStockChips.some((c) => Number(issued[c.denomination]) > c.quantity) ||
                  promoStockChips.some((c) => Number(promoIssued[c.denomination]) > c.quantity)
                }
              >
                {loading ? "Saving..." : "Issue Chips"}
              </Button>
            </Box>
          </Grid>
        </Grid>

      </Box>
    </Drawer>
    </>
  );
}
